import { useState, useEffect, useCallback } from "react";
import {
  CostingMethod,
  CostingMethodEnum,
  CostingMethodLabel,
  CostingMethodSchema,
  DEFAULT_ITEM_ADVANCED_SETTINGS,
  ItemComponent,
  ITEM_UNIT_ABBREVIATION,
  UpdateItemParams,
} from "@forgepoint/shared";
import { useComponentItemSynced, useItemsStore } from "./itemsStore";
import { baseUnitCost, withBaseUnitCost } from "./bomEdits";
import { parseDecimal } from "./utils";
import ItemEditor from "./ItemEditor";
import { ItemSelector } from "@/components/ItemSelector";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "@/components/ui/sheet";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Edit, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { useFeatureFlag } from "@/store/featureFlagStore";
import { useItemSettingsStore } from "@/store/itemSettingsStore";

interface BOMComponentSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: "add" | "edit";
  component?: ItemComponent;
  componentItemId?: string;
  onSave: (component: ItemComponent) => Promise<boolean>;
  onRemove?: () => void;
  onUpdated?: () => void;
  parentItemId?: string;
  /** Quantities the component item opens with in the item editor. */
  quantities?: string[];
  /** Freezes the fields this BOM owns; the component item stays editable. */
  locked?: boolean;
  lockReason?: string;
  /** When provided, opening the component item navigates within the parent sheet. */
  onNavigateToItem?: (itemId: string) => void;
}

/**
 * Unified sheet component for adding or editing BOM components.
 * Handles both creating new item components and modifying existing ones, and
 * hides the fields the org's item settings disallow. Override cost shows
 * only when both the org's cost override toggle and its advanced setting allow it.
 * @param props - The component, sheet mode, and save handlers
 * @returns The BOM component sheet
 */
export default function BOMComponentSheet({
  open,
  onOpenChange,
  mode,
  component,
  componentItemId,
  onSave,
  onRemove,
  onUpdated,
  parentItemId,
  quantities,
  locked = false,
  lockReason,
  onNavigateToItem,
}: BOMComponentSheetProps) {
  const { items, updateItem } = useItemsStore();
  const epicorPrimitiveSupportEnabled = useFeatureFlag(
    "epicor-primitive-support",
  );
  const itemSettings =
    useItemSettingsStore((state) => state.settings) ??
    DEFAULT_ITEM_ADVANCED_SETTINGS;
  const showOverrideCost = itemSettings.allowComponentCostOverride;
  const fixedQuantityAllowed = itemSettings.allowComponentFixedQuantity;
  const scrapAllowed = itemSettings.allowComponentScrap;
  const operationLinksAllowed = itemSettings.allowComponentOperationLinks;
  const costingMethodOverrideAllowed =
    itemSettings.allowComponentCostingMethodOverride;

  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [quantity, setQuantity] = useState("");
  const [costingMethod, setCostingMethod] = useState<CostingMethod | null>(
    null,
  );
  const [reasoning, setReasoning] = useState("");
  const [fixedQuantity, setFixedQuantity] = useState(false);
  const [scrapPercent, setScrapPercent] = useState("");
  const [overrideComponentCost, setOverrideComponentCost] = useState("");
  const [itemCost, setItemCost] = useState("");
  const [relatedItemOperationId, setRelatedItemOperationId] = useState<
    string | null
  >(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isItemEditorOpen, setIsItemEditorOpen] = useState(false);

  const isEditMode = mode === "edit";
  const itemId = componentItemId || component?.itemId;
  const selectedItem = selectedItemId ? items.get(selectedItemId) : null;
  const componentItem = itemId ? items.get(itemId) : null;
  const parentItem = parentItemId ? items.get(parentItemId) : null;
  const selectedItemCostingMethod = selectedItem?.costingMethod;
  const hasUnitCost =
    selectedItem?.costBreaks?.some((costBreak) => costBreak.minQty === 1) ??
    false;
  const hasBOM =
    (selectedItem?.operations.length ?? 0) > 0 ||
    (selectedItem?.components.length ?? 0) > 0;

  // The item cost belongs to the component item, so it follows that item's own
  // locks rather than this BOM's.
  const isComponentSynced = useComponentItemSynced(parentItemId, itemId);
  const storedItemCost = baseUnitCost(componentItem?.costBreaks);
  const effectiveCostingMethod = costingMethod ?? componentItem?.costingMethod;
  // Re-pointing the row to another item makes the target of a cost edit
  // ambiguous, so the field only shows while the row still holds its own item.
  const showItemCost =
    isEditMode &&
    Boolean(componentItem) &&
    selectedItemId === component?.itemId &&
    effectiveCostingMethod === CostingMethodEnum.UNIT_COST;
  const isItemCostEditable = showItemCost && !isComponentSynced;
  const hasCostBreaks = (componentItem?.costBreaks?.length ?? 0) > 1;

  useEffect(() => {
    if (open && !isSubmitting) {
      if (isEditMode && component) {
        setSelectedItemId(component.itemId);
        setQuantity(component.quantity);
        setCostingMethod(component.costingMethod ?? null);
        setReasoning(component.reasoning || "");
        setFixedQuantity(component.fixedQuantity ?? false);
        setScrapPercent(component.scrapPercent ?? "");
        setOverrideComponentCost(component.overrideComponentCost ?? "");
        setRelatedItemOperationId(component.relatedItemOperationId ?? null);
      } else {
        setSelectedItemId(null);
        setQuantity("");
        setCostingMethod(null);
        setReasoning("");
        setFixedQuantity(false);
        setScrapPercent("");
        setOverrideComponentCost("");
        setRelatedItemOperationId(null);
      }
    }
  }, [open, isEditMode, component, isSubmitting]);

  // The component item can arrive after the sheet opens, so the cost field
  // fills on its own rather than resetting the fields this BOM owns.
  useEffect(() => {
    if (open && !isSubmitting) {
      setItemCost(storedItemCost);
    }
  }, [open, storedItemCost, isSubmitting]);

  const handleSave = async () => {
    if (!selectedItemId) {
      toast.error("Please select an item");
      return;
    }
    if (!quantity || parseFloat(quantity) <= 0) {
      toast.error("Please enter a valid quantity");
      return;
    }
    if (scrapPercent && parseFloat(scrapPercent) < 0) {
      toast.error("Scrap percent cannot be negative");
      return;
    }
    if (overrideComponentCost && parseFloat(overrideComponentCost) < 0) {
      toast.error("Override cost cannot be negative");
      return;
    }
    const hasItemCostChange = isItemCostEditable && itemCost !== storedItemCost;
    const hasBomChanges =
      !isEditMode ||
      !component ||
      selectedItemId !== component.itemId ||
      quantity !== component.quantity ||
      costingMethod !== (component.costingMethod ?? null) ||
      fixedQuantity !== (component.fixedQuantity ?? false) ||
      scrapPercent !== (component.scrapPercent ?? "") ||
      overrideComponentCost !== (component.overrideComponentCost ?? "") ||
      relatedItemOperationId !== (component.relatedItemOperationId ?? null) ||
      reasoning !== (component.reasoning || "");

    const parsedItemCost = parseDecimal(itemCost);
    if (hasItemCostChange && (!parsedItemCost || parsedItemCost.lessThan(0))) {
      toast.error("Please enter a valid item cost");
      return;
    }

    if (!hasBomChanges && !hasItemCostChange) {
      onOpenChange(false);
      return;
    }

    setIsSubmitting(true);
    try {
      // The item cost goes first: a failure then leaves this BOM untouched.
      if (hasItemCostChange && !(await saveItemCost())) {
        return;
      }
      if (hasBomChanges && !(await saveComponent(selectedItemId))) {
        return;
      }
      onOpenChange(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const saveComponent = (selectedId: string): Promise<boolean> =>
    onSave({
      ...component,
      itemId: selectedId,
      quantity,
      costingMethod,
      fixedQuantity,
      scrapPercent: scrapPercent || undefined,
      overrideComponentCost: overrideComponentCost || undefined,
      relatedItemOperationId,
      reasoning,
      description: selectedItem?.name || "",
    });

  /** Writes the component item's base cost break; every BOM using it changes. */
  const saveItemCost = async (): Promise<boolean> => {
    if (!itemId) {
      return false;
    }
    const { item, error } = await updateItem(itemId, {
      costBreaks: withBaseUnitCost(componentItem?.costBreaks, itemCost),
    });
    if (!item) {
      toast.error(error ?? "Failed to update the item cost");
      return false;
    }
    toast.success(`Updated the item cost on ${item.name}`);
    onUpdated?.();
    return true;
  };

  const handleDelete = async () => {
    if (!onRemove) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onRemove();
      onOpenChange(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  /** Opens the component item, in this sheet's parent when it can navigate. */
  const openComponentItem = () => {
    onOpenChange(false);
    if (onNavigateToItem && itemId) {
      onNavigateToItem(itemId);
      return;
    }
    setIsItemEditorOpen(true);
  };

  const handleItemUpdate = useCallback(
    async (data: UpdateItemParams): Promise<true | string> => {
      if (!itemId) {
        return "No item selected for editing";
      }
      const { item, error } = await updateItem(itemId, data);
      if (item) {
        onUpdated?.();
        return true;
      }
      return error ?? "Failed to update item";
    },
    [updateItem, itemId, onUpdated],
  );

  const title = isEditMode ? "Edit Component" : "Add Component";
  const description = locked
    ? `${lockReason ?? "These values are read-only"}. The component item itself is still editable.`
    : isEditMode
      ? "Modify the component details or remove it from the BOM"
      : "Select a component item and specify the quantity needed";
  const saveButtonText = isEditMode ? "Save Changes" : "Add Component";
  const canSave = !locked || isItemCostEditable;

  return (
    <>
      <Sheet
        open={open}
        onOpenChange={(newOpen) => !isSubmitting && onOpenChange(newOpen)}
      >
        <SheetContent className="w-[600px] sm:max-w-[600px] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{title}</SheetTitle>
            <SheetDescription>{description}</SheetDescription>
          </SheetHeader>

          <div className="grid gap-4 py-4">
            <fieldset disabled={locked} className="contents">
              <div className="grid gap-2">
                <Label htmlFor="component-item">Component Item</Label>
                <ItemSelector
                  currentlySelectedId={selectedItemId}
                  onSelectId={setSelectedItemId}
                  placeholder="Select a component..."
                  excludeItemId={parentItemId}
                  enableQuickCreate
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="quantity">
                  {fixedQuantity
                    ? "Fixed Quantity Per Build"
                    : "Quantity Per Parent"}
                </Label>
                <Input
                  id="quantity"
                  type="number"
                  step="0.001"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  placeholder="1.000"
                  disabled={isSubmitting}
                />
              </div>

              {showOverrideCost && (
                <div className="grid gap-2">
                  <Label htmlFor="override-component-cost">Override cost</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                      $
                    </span>
                    <Input
                      id="override-component-cost"
                      type="number"
                      step="0.01"
                      min="0"
                      value={overrideComponentCost}
                      onChange={(event) =>
                        setOverrideComponentCost(event.target.value)
                      }
                      placeholder="0.00"
                      disabled={isSubmitting}
                      className="pl-7"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Cost per component unit. Replaces the component's own
                    rolled-up cost.
                  </p>
                </div>
              )}

              {epicorPrimitiveSupportEnabled && (
                <>
                  {fixedQuantityAllowed && (
                    <div className="flex items-center justify-between gap-4">
                      <div className="space-y-0.5">
                        <Label htmlFor="fixed-quantity">Fixed Quantity</Label>
                        <p className="text-sm text-muted-foreground">
                          Use this quantity once for the entire build instead of
                          once per parent item.
                        </p>
                      </div>
                      <Switch
                        id="fixed-quantity"
                        checked={fixedQuantity}
                        onCheckedChange={setFixedQuantity}
                        disabled={isSubmitting}
                      />
                    </div>
                  )}

                  {scrapAllowed && (
                    <div className="grid gap-2">
                      <Label htmlFor="scrap-percent">Scrap (%)</Label>
                      <Input
                        id="scrap-percent"
                        type="number"
                        step="any"
                        min="0"
                        value={scrapPercent}
                        onChange={(event) =>
                          setScrapPercent(event.target.value)
                        }
                        placeholder="0"
                        disabled={isSubmitting}
                      />
                    </div>
                  )}

                  {operationLinksAllowed && (
                    <div className="grid gap-2">
                      <Label htmlFor="related-operation">
                        Related Operation
                      </Label>
                      <Select
                        value={relatedItemOperationId ?? "BEGINNING"}
                        onValueChange={(value) =>
                          setRelatedItemOperationId(
                            value === "BEGINNING" ? null : value,
                          )
                        }
                        disabled={isSubmitting}
                      >
                        <SelectTrigger id="related-operation">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="BEGINNING">
                            Beginning of job
                          </SelectItem>
                          {parentItem?.operations.flatMap((operation, index) =>
                            operation.id
                              ? [
                                  <SelectItem
                                    key={operation.id}
                                    value={operation.id}
                                  >
                                    Step {index + 1}:{" "}
                                    {operation.description ?? "Operation"}
                                  </SelectItem>,
                                ]
                              : [],
                          )}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        Identifies when this material is required in the parent
                        BOM.
                      </p>
                    </div>
                  )}

                  {costingMethodOverrideAllowed && (
                    <div className="grid gap-2">
                      <Label htmlFor="component-costing-method">
                        Costing Method
                      </Label>
                      <Select
                        value={costingMethod ?? "DEFAULT"}
                        onValueChange={(value) => {
                          if (value === "DEFAULT") {
                            setCostingMethod(null);
                            return;
                          }
                          const parsed = CostingMethodSchema.safeParse(value);
                          if (parsed.success) {
                            setCostingMethod(parsed.data);
                          }
                        }}
                        disabled={isSubmitting || !selectedItem}
                      >
                        <SelectTrigger id="component-costing-method">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="DEFAULT">
                            Item default
                            {selectedItemCostingMethod
                              ? ` (${CostingMethodLabel[selectedItemCostingMethod]})`
                              : ""}
                          </SelectItem>
                          <SelectItem
                            value={CostingMethodEnum.UNIT_COST}
                            disabled={!hasUnitCost}
                          >
                            Unit Cost
                          </SelectItem>
                          <SelectItem
                            value={CostingMethodEnum.BOM}
                            disabled={!hasBOM}
                          >
                            BOM
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        Item default follows the costing method selected on the
                        component item.
                      </p>
                    </div>
                  )}
                </>
              )}

              <div className="grid gap-2">
                <Label htmlFor="reasoning">Notes (Optional)</Label>
                <Textarea
                  id="reasoning"
                  value={reasoning}
                  onChange={(e) => setReasoning(e.target.value)}
                  placeholder="Any additional notes..."
                  disabled={isSubmitting}
                />
              </div>
            </fieldset>

            {showItemCost && componentItem && (
              <div className="grid gap-2 pt-4 border-t">
                <Label htmlFor="item-cost">
                  Item cost ($/{ITEM_UNIT_ABBREVIATION[componentItem.unit]})
                </Label>
                <Input
                  id="item-cost"
                  type="number"
                  step="any"
                  min="0"
                  value={itemCost}
                  onChange={(event) => setItemCost(event.target.value)}
                  placeholder="0.00"
                  disabled={isSubmitting || !isItemCostEditable}
                />
                <p className="text-xs text-muted-foreground">
                  {isItemCostEditable
                    ? `Changes the cost on ${componentItem.name}, not this BOM. Every BOM that uses the item is affected.`
                    : "Controlled by integration. Open the item to change its cost."}
                  {hasCostBreaks &&
                    " This item has quantity breaks; this sets the base cost."}
                </p>
              </div>
            )}

            {isEditMode && componentItem && (
              <div className="pt-4 border-t">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={openComponentItem}
                  disabled={isSubmitting}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit {componentItem.name} Item
                </Button>
              </div>
            )}
          </div>

          <SheetFooter className="flex justify-between">
            {isSubmitting ? (
              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground w-full">
                <Loader2 className="h-4 w-4 animate-spin" />
                Processing...
              </div>
            ) : (
              <>
                {isEditMode && onRemove && !locked ? (
                  <Button
                    variant="destructive"
                    onClick={handleDelete}
                    disabled={isSubmitting}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Remove Component
                  </Button>
                ) : (
                  <div />
                )}
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => onOpenChange(false)}
                    disabled={isSubmitting}
                  >
                    {canSave ? "Cancel" : "Close"}
                  </Button>
                  {canSave && (
                    <Button onClick={handleSave} disabled={isSubmitting}>
                      {saveButtonText}
                    </Button>
                  )}
                </div>
              </>
            )}
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {isEditMode && itemId && (
        <ItemEditor
          open={isItemEditorOpen}
          onOpenChange={setIsItemEditorOpen}
          itemId={itemId}
          onSave={handleItemUpdate}
          mode="edit"
          quantities={quantities}
          onBomUpdated={onUpdated}
          defaultTab="bom"
        />
      )}
    </>
  );
}
